/**
 * Ordres de mission à valider, côté RH — M28 (retour Manon, mi-parcours).
 * Le déplacement préparé par l'agent (souvent depuis une formation acceptée)
 * arrive ici ; la RH valide ou refuse (motivé). Masquée s'il n'y a rien.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Input } from '@/components/ui';
import { api, ApiException, type DeplacementDTO } from '@/lib/api';

const TRANSPORT: Record<string, string> = {
  vehicule_service: 'véhicule de service', vehicule_personnel: 'véhicule personnel',
  train: 'train', covoiturage: 'covoiturage', autre: 'autre',
};
const fr = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' });

export function DeplacementsRhCard() {
  const [items, setItems] = useState<DeplacementDTO[]>([]);
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try { setItems(await api.rh.deplacements('a_valider')); } catch { setItems([]); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function statuer(id: string, statut: 'valide' | 'refuse') {
    setErr(null);
    try { await api.rh.statuerDeplacement(id, { statut, motif: motifs[id]?.trim() || null }); await charger(); }
    catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Décision impossible'); }
  }

  if (items.length === 0) return null;
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="font-display text-base font-semibold text-ink">Ordres de mission à valider</h2>
        <Badge variant="urgent">{items.length}</Badge>
      </div>
      {err && <p className="mb-2 text-xs text-urgent" role="alert">{err}</p>}
      <ul className="space-y-2">
        {items.map((d) => (
          <li key={d.id} className="rounded-r-md border border-border/60 p-3 text-sm">
            <p className="text-ink"><b>{d.agentNom ?? 'Agent'}</b> — {d.objet}</p>
            <p className="text-xs text-ink-muted">
              {d.lieu} · {fr(d.dateDepart)}{d.dateRetour !== d.dateDepart ? ` → ${fr(d.dateRetour)}` : ''} · {TRANSPORT[d.transport] ?? d.transport}
              {d.kmEstimes ? ` · ${d.kmEstimes} km estimés` : ''}
            </p>
            {d.commentaire && <p className="text-xs text-ink-muted">« {d.commentaire} »</p>}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input className="min-w-[220px] flex-1" value={motifs[d.id] ?? ''} maxLength={2000}
                placeholder="Motif (obligatoire pour un refus)" aria-label="Motif"
                onChange={(e) => setMotifs((m) => ({ ...m, [d.id]: e.target.value }))} />
              <Button size="sm" variant="rh" onClick={() => void statuer(d.id, 'valide')}>Valider</Button>
              <Button size="sm" variant="ghost" onClick={() => void statuer(d.id, 'refuse')}>Refuser</Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
